import { ref, onScopeDispose } from 'vue';
import { buildShareLink, isHttpShareLink } from '/app/utils/social-networks';

/**
 * Composable for opening social network share popups.
 *
 * SSR-safe: all `window` access is inside the returned functions, never at
 * module load. The composable itself can be safely instantiated during SSR;
 * `share()` and `touch()` are no-ops if called server-side (no window).
 *
 * Returns:
 *   share(network, content, options) - Opens a popup for HTTP-style networks.
 *   touch(network, content)          - Opens a new tab/window for non-HTTP schemes.
 *   isOpen                           - Ref<boolean>: true while a popup is open.
 *
 * Options for share():
 *   width, height          - Popup dimensions (defaults: 626 x 436).
 *   onOpen(network, url)   - Called when the popup opens successfully.
 *   onClose(network, url)  - Called when the popup is detected closed.
 *   onChange(network, url) - Called when an existing popup is replaced by a new one.
 *   onFailure(network, url)- Called when the popup is blocked / never opens.
 */
export function useSocialShare() {
    const isOpen = ref(false);

    let popupWindow = null;
    let popupInterval = null;

    function clearPolling() {
        if (popupInterval) {
            clearInterval(popupInterval);
            popupInterval = null;
        }
    }

    function share(network, content, options = {}) {
        if (typeof window === 'undefined') return;

        const {
            width = 626,
            height = 436,
            onOpen,
            onClose,
            onChange,
            onFailure,
        } = options;

        const link = buildShareLink(network, content);
        const key = String(network).toLowerCase();

        // Center popup across multi-monitor setups.
        const screenWidth = window.innerWidth || document.documentElement.clientWidth || window.screenX;
        const screenHeight = window.innerHeight || document.documentElement.clientHeight || window.screenY;
        const systemZoom = screenWidth / window.screen.availWidth;
        const popupLeft = (screenWidth - width) / 2 / systemZoom + (window.screenLeft ?? window.screenX);
        const popupTop = (screenHeight - height) / 2 / systemZoom + (window.screenTop ?? window.screenY);

        // If a popup is already open, close it and signal change.
        if (popupWindow && popupInterval) {
            clearPolling();
            popupWindow.close();
            onChange?.(key, link);
        }

        popupWindow = window.open(
            link,
            'sharer-' + key,
            ',height=' + height
            + ',width=' + width
            + ',left=' + popupLeft
            + ',top=' + popupTop
            + ',screenX=' + popupLeft
            + ',screenY=' + popupTop,
        );

        if (!popupWindow) {
            isOpen.value = false;
            onFailure?.(key, link);
            return;
        }

        popupWindow.focus();
        isOpen.value = true;

        popupInterval = setInterval(() => {
            if (!popupWindow || popupWindow.closed) {
                clearPolling();
                popupWindow = null;
                isOpen.value = false;
                onClose?.(key, link);
            }
        }, 500);

        onOpen?.(key, link);
    }

    function touch(network, content, options = {}) {
        if (typeof window === 'undefined') return;

        const link = buildShareLink(network, content);
        const key = String(network).toLowerCase();

        window.open(link, '_blank');

        options.onOpen?.(key, link);
    }

    // Clean up the interval if the consumer unmounts mid-popup.
    onScopeDispose(() => {
        clearPolling();
        popupWindow = null;
    });

    return {
        share,
        touch,
        isOpen,
    };
}

/**
 * Re-export so callers can decide whether a network needs popup handling
 * (HTTP) or just a new tab (mailto:, sms:, fb-messenger:, viber:).
 */
export { isHttpShareLink, buildShareLink };
